import type { LatestRvfcObservation } from '../decodeHintCollector';
import type { PresentationSummary } from './streamQualityContracts';
import type { FramePresentationEvidence, FrameTimingSource, PlaybackIntent, PlaybackVisibility } from '../streamRecovery/contracts';

/** A gap this long is three missed frames at 60 fps: a visible hitch, not jitter. */
export const PRESENT_STALL_GAP_MS = 50;

/** Cumulative `HTMLVideoElement.getVideoPlaybackQuality()` counters. */
export type PlaybackQualityCounters = { droppedVideoFrames: number; totalVideoFrames: number };

export type PresentationQualityTrackerOptions = {
  /** Must share rVFC's clock (the `now` argument is a DOMHighResTimeStamp). */
  nowMs?: () => number;
};

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Measures what actually reached the glass, on every engine that runs rVFC — Safari included.
 *
 * Decode rate says nothing about presentation: a Safari guest decoded 60 fps while presenting
 * 36–40 because late frames were discarded after decode. This tracker counts presentations,
 * the gaps between them, and the element's own dropped-frame counter, independently of the
 * rVFC timing fields (`receiveTime` etc.) that Safari often leaves empty.
 */
export class PresentationQualityTracker {
  private readonly nowMs: () => number;
  private tickStartMs: number | null = null;
  private callbacks = 0;
  private stallCount = 0;
  private maxGapMs: number | undefined;
  private lastPresentationMs: number | undefined;
  private lastPresentedFrames: number | undefined;
  private compositorDelta: number | undefined;
  private lastPlayback: PlaybackQualityCounters | undefined;
  private evidencePlayback: PlaybackQualityCounters | undefined;
  private progressSequence = 0;
  private lastCallbackMs: number | undefined;
  private lastObservationSequence: number | undefined;
  private timingSource: FrameTimingSource = 'unavailable';

  constructor(options: PresentationQualityTrackerOptions = {}) {
    this.nowMs = options.nowMs ?? defaultNow;
  }

  observe(observation: Pick<LatestRvfcObservation, 'decodeWallMs'> & Partial<LatestRvfcObservation>): void {
    // Keep counting callbacks on engines without presentation metadata, but do not feed
    // callback scheduling delay into the frame pipeline's receive-to-present timing.
    const presentedAt = observation.presentationTime ?? observation.displayNow;
    if (!isFiniteNumber(presentedAt)) return;
    if (observation.observationSequence !== undefined && this.lastObservationSequence !== undefined &&
        observation.observationSequence <= this.lastObservationSequence) return;
    this.lastObservationSequence = observation.observationSequence;
    this.lastCallbackMs = this.nowMs();
    this.timingSource = observation.timingSource ?? (observation.presentationTime !== undefined ? 'presentation-time' : 'callback-time');
    if (this.tickStartMs === null) this.tickStartMs = this.nowMs();
    this.callbacks += 1;
    if (this.lastPresentationMs !== undefined) {
      const gap = presentedAt - this.lastPresentationMs;
      if (gap > 0) {
        this.maxGapMs = Math.max(this.maxGapMs ?? 0, gap);
        if (gap >= PRESENT_STALL_GAP_MS) this.stallCount += 1;
      }
    }
    this.lastPresentationMs = presentedAt;
    this.progressSequence += 1;
    this.observePresentedFrames(observation.presentedFrames);
  }

  /** Summarize the interval since the previous call; undefined until a frame was ever presented. */
  summarizeAndReset(playback?: PlaybackQualityCounters): PresentationSummary | undefined {
    const playbackDelta = this.playbackDelta(playback);
    if (this.tickStartMs === null) return undefined;
    const now = this.nowMs();
    const elapsedMs = Math.max(0, now - this.tickStartMs);
    const summary: PresentationSummary = {
      elapsedMs: Math.round(elapsedMs),
      callbacks: this.callbacks,
      presentedFps: elapsedMs > 0 ? round1(((this.compositorDelta ?? this.callbacks) * 1000) / elapsedMs) : 0,
      stallCount: this.stallCount,
      ...(this.maxGapMs !== undefined ? { maxPresentGapMs: round1(this.maxGapMs) } : {}),
      ...(this.lastPresentationMs !== undefined
        ? { sinceLastPresentMs: round1(Math.max(0, now - this.lastPresentationMs)) }
        : {}),
      ...(this.compositorDelta !== undefined ? { compositorPresentedDelta: this.compositorDelta } : {}),
      ...playbackDelta,
    };
    this.tickStartMs = now;
    this.callbacks = 0;
    this.stallCount = 0;
    this.maxGapMs = undefined;
    this.compositorDelta = undefined;
    return summary;
  }

  /** Cumulative, non-destructive evidence; repeated reads do not manufacture progress. */
  snapshotEvidence(input: {
    visibility: PlaybackVisibility; playback: PlaybackIntent;
    decodedFrames?: number; decodeObservation?: FramePresentationEvidence['decodeObservation'];
    playbackQuality?: PlaybackQualityCounters;
  }): FramePresentationEvidence {
    const now = this.nowMs();
    const playback = input.playbackQuality;
    if (playback && isFiniteNumber(playback.totalVideoFrames) && isFiniteNumber(playback.droppedVideoFrames)) {
      const previous = this.evidencePlayback;
      const shown = playback.totalVideoFrames - playback.droppedVideoFrames;
      if (previous && playback.totalVideoFrames >= previous.totalVideoFrames &&
          playback.droppedVideoFrames >= previous.droppedVideoFrames &&
          shown > previous.totalVideoFrames - previous.droppedVideoFrames) {
        this.progressSequence += 1;
        this.lastPresentationMs = now;
      }
      this.evidencePlayback = { ...playback };
    }
    return {
      visibility: input.visibility, playback: input.playback, progressSequence: this.progressSequence,
      timingSource: this.timingSource,
      ...(this.lastPresentationMs !== undefined ? { lastPresentationAgeMs: Math.max(0, now - this.lastPresentationMs) } : {}),
      ...(this.lastCallbackMs !== undefined ? { callbackAgeMs: Math.max(0, now - this.lastCallbackMs) } : {}),
      ...(isFiniteNumber(input.decodedFrames) ? { decodedFrames: input.decodedFrames } : {}),
      ...(isFiniteNumber(input.decodedFrames) && input.decodeObservation
        ? { decodeObservation: { ...input.decodeObservation } } : {}),
      ...(this.lastPresentedFrames !== undefined ? { presentedFrames: this.lastPresentedFrames } : {}),
      ...(this.evidencePlayback ? { playbackTotalFrames: this.evidencePlayback.totalVideoFrames,
        playbackDroppedFrames: this.evidencePlayback.droppedVideoFrames } : {}),
    };
  }

  /** Forget every baseline: a new connection or a swapped video element. */
  reset(): void {
    this.tickStartMs = null;
    this.callbacks = 0;
    this.stallCount = 0;
    this.maxGapMs = undefined;
    this.lastPresentationMs = undefined;
    this.lastPresentedFrames = undefined;
    this.compositorDelta = undefined;
    this.lastPlayback = undefined;
    this.evidencePlayback = undefined;
    this.progressSequence = 0;
    this.lastCallbackMs = undefined;
    this.lastObservationSequence = undefined;
    this.timingSource = 'unavailable';
  }

  private observePresentedFrames(presentedFrames: number | undefined): void {
    if (!isFiniteNumber(presentedFrames)) return;
    const previous = this.lastPresentedFrames;
    this.lastPresentedFrames = presentedFrames;
    if (previous === undefined || presentedFrames < previous) return;
    this.compositorDelta = (this.compositorDelta ?? 0) + (presentedFrames - previous);
  }

  private playbackDelta(
    playback: PlaybackQualityCounters | undefined,
  ): Pick<PresentationSummary, 'playbackDroppedDelta' | 'playbackTotalDelta'> {
    if (!playback || !isFiniteNumber(playback.droppedVideoFrames) || !isFiniteNumber(playback.totalVideoFrames)) {
      return {};
    }
    const previous = this.lastPlayback;
    this.lastPlayback = playback;
    if (!previous) return {};
    const dropped = playback.droppedVideoFrames - previous.droppedVideoFrames;
    const total = playback.totalVideoFrames - previous.totalVideoFrames;
    // A counter that went backwards is a new element or a reset, not a negative drop.
    if (dropped < 0 || total < 0) return {};
    return { playbackDroppedDelta: dropped, playbackTotalDelta: total };
  }
}
